import { formatContractHubDetail } from "./contractHubApiErrors";

export const ACCESS_TOKEN_KEY = "accessToken";
export const API_BASE_URL_KEY = "apiBaseUrl";

/** Header name aligned with Apex ``cenna_logging`` (``X-Request-Id``). */
export const REQUEST_ID_HEADER = "X-Request-Id";

/** Mint a per-request correlation id for API calls. */
export function createRequestId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/**
 * Webpack dev-server proxy routes (see webpack.config.js):
 *   /apex/** → http://localhost:8100  (Apex — auth; path prefix stripped)
 *   /api/** (incl. /api/assistant SSE), /auth, /contracts, /chat, /knowledge-base → :8000 (Contract Hub)
 *
 * Using relative paths keeps every fetch same-origin HTTPS, which is required
 * by WKWebView (macOS Office Add-in) — direct http:// fetches are blocked.
 *
 * After configurable login, {@link setStoredApiBaseUrl} stores the backend origin
 * from the login response so requests use that base instead of same-origin proxy.
 */
export const APEX_PROXY_PREFIX = "/apex";

/**
 * URL actually used for `fetch(login)`. Relative paths stay same-origin (HTTPS + webpack proxy).
 *
 * Rewrites `http://localhost:8100/...` (and `127.0.0.1:8100`) to `/apex/...` because the task
 * pane is served over **HTTPS**; calling plain **http://** from that page is blocked as mixed
 * content (“Load failed” / “Failed to fetch” in WebKit / Office).
 */
export function normalizeLoginFetchUrl(input: string): string {
  const t = input.trim();
  if (!t) {
    return t;
  }
  if (t.startsWith("/")) {
    return t;
  }
  let u: URL;
  try {
    u = new URL(t);
  } catch {
    throw new Error("Invalid login URL.");
  }
  const local =
    u.protocol === "http:" && (u.hostname === "localhost" || u.hostname === "127.0.0.1");
  if (local && u.port === "8100") {
    return `${APEX_PROXY_PREFIX}${u.pathname}${u.search}`;
  }
  return u.href;
}

const LOGIN_API_PATH = "/api/auth/login";
const SSO_CONFIG_PATH = "/api/auth/sso/config";
const SSO_CALLBACK_PATH = "/api/auth/sso/callback";

/** Normalize the login base URL, strip trailing slashes, then append the given path suffix. */
function buildFetchUrlWithPath(input: string, pathSuffix: string): string {
  const normalized = normalizeLoginFetchUrl(input.trim());
  const base = normalized.replace(/\/+$/, "") || normalized;
  return base.endsWith(pathSuffix) ? base : `${base}${pathSuffix}`;
}

/**
 * URL used for the login ``POST``: normalizes mixed-content localhost, strips trailing ``/``,
 * then appends ``/api/auth/login``. If the path is already present, it is not added twice.
 */
export function buildLoginFetchUrl(input: string): string {
  return buildFetchUrlWithPath(input, LOGIN_API_PATH);
}

/** URL for ``GET /api/auth/sso/config``. */
export function buildSsoConfigFetchUrl(input: string): string {
  return buildFetchUrlWithPath(input, SSO_CONFIG_PATH);
}

/** URL for ``POST /api/auth/sso/callback``. */
export function buildSsoCallbackFetchUrl(input: string): string {
  return buildFetchUrlWithPath(input, SSO_CALLBACK_PATH);
}

export function getStoredAccessToken(): string | null {
  try {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setStoredAccessToken(token: string | null): void {
  try {
    if (token) {
      localStorage.setItem(ACCESS_TOKEN_KEY, token);
    } else {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
    }
  } catch {
    // ignore
  }
}

function normalizeApiBaseUrl(base: string): string {
  const t = base.trim().replace(/\/+$/, "");
  return t;
}

/** Local Contract Hub HTTP ports that must use the HTTPS webpack proxy. */
const LOCAL_DEV_BACKEND_PORTS = new Set(["8000", "8301"]);

/**
 * From an HTTPS task pane, ``http://localhost:8000`` (Contract Hub) is mixed content
 * and often blocked; it also skips the webpack dev proxy so CORS may fail. Drop to
 * relative URLs so ``/auth``, ``/contracts``, etc. stay same-origin.
 */
export function normalizeBackendBaseUrlForAddIn(base: string | null): string | null {
  if (!base || !base.trim()) {
    return null;
  }
  const t = normalizeApiBaseUrl(base);
  if (typeof window === "undefined" || window.location.protocol !== "https:") {
    return t;
  }
  try {
    const u = new URL(t);
    if (
      u.protocol === "http:" &&
      (u.hostname === "localhost" || u.hostname === "127.0.0.1") &&
      LOCAL_DEV_BACKEND_PORTS.has(u.port)
    ) {
      return null;
    }
  } catch {
    return t;
  }
  return t;
}

export function getStoredApiBaseUrl(): string | null {
  try {
    const raw = localStorage.getItem(API_BASE_URL_KEY);
    if (!raw || !raw.trim()) {
      return null;
    }
    return normalizeApiBaseUrl(raw);
  } catch {
    return null;
  }
}

export function setStoredApiBaseUrl(base: string | null): void {
  try {
    const resolved = normalizeBackendBaseUrlForAddIn(base);
    if (resolved && resolved.trim()) {
      localStorage.setItem(API_BASE_URL_KEY, resolved);
    } else {
      localStorage.removeItem(API_BASE_URL_KEY);
    }
  } catch {
    // ignore
  }
}

/**
 * Keeps absolute URLs unchanged; prepends stored API base when set; else relative (dev proxy).
 *
 * Dev (no base): ``/auth/me`` and ``/contracts/...`` hit the task-pane origin; webpack
 * rewrites ``/auth`` → ``/api/auth``, etc.
 *
 * Staged/prod base often ends with ``/api`` (from Apex ``backend_base_url``). Join without
 * duplicating ``/api`` — e.g. base ``https://ch.example.com/api`` + ``/auth/me`` →
 * ``.../api/auth/me``, not ``.../api/api/auth/me``.
 */
export function resolveApiUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) {
    return path;
  }
  const rel = path.startsWith("/") ? path : `/${path}`;
  const base = getStoredApiBaseUrl();
  if (!base) {
    return rel;
  }
  if (!/^https?:\/\//i.test(base)) {
    return `${base}${rel}`;
  }

  const baseNorm = normalizeApiBaseUrl(base);
  const baseHasApiSuffix = /\/api$/i.test(baseNorm);

  if (rel.startsWith("/api/")) {
    if (baseHasApiSuffix) {
      return `${baseNorm}${rel.slice(4)}`;
    }
    return `${baseNorm}${rel}`;
  }

  if (baseHasApiSuffix) {
    return `${baseNorm}${rel}`;
  }

  return `${baseNorm}/api${rel}`;
}

export type ApiRequestOptions = RequestInit & { jsonBody?: unknown; skipAuth?: boolean };

/** Parse JSON error bodies (e.g. FastAPI ``{ "detail": ... }``) for display. */
export async function readErrorMessage(response: Response): Promise<string> {
  const base = `Request failed (${response.status})`;
  try {
    const text = await response.text();
    if (!text.trim()) {
      return base;
    }
    try {
      const j = JSON.parse(text) as Record<string, unknown>;
      const detail = j.detail ?? j.message ?? j.error;
      const fromDetail = formatContractHubDetail(detail);
      if (fromDetail) {
        return fromDetail;
      }
      if (Array.isArray(detail)) {
        const parts = detail
          .map((item) => {
            if (typeof item === "string") {
              return item.trim();
            }
            if (item && typeof item === "object" && "msg" in item) {
              const msg = (item as Record<string, unknown>).msg;
              return typeof msg === "string" ? msg.trim() : "";
            }
            return "";
          })
          .filter((s) => s.length > 0);
        if (parts.length > 0) {
          return parts.join("; ");
        }
      }
      return text.trim();
    } catch {
      return text.trim();
    }
  } catch {
    return base;
  }
}

/**
 * ``fetch`` to a platform API path with ``X-Request-Id`` for log correlation.
 * Absolute URLs are passed through unchanged.
 */
export async function fetchApi(pathOrUrl: string, init: RequestInit = {}): Promise<Response> {
  const url = /^https?:\/\//i.test(pathOrUrl) ? pathOrUrl : resolveApiUrl(pathOrUrl);
  const headers = new Headers(init.headers);
  if (!headers.has(REQUEST_ID_HEADER)) {
    headers.set(REQUEST_ID_HEADER, createRequestId());
  }
  return fetch(url, { ...init, headers });
}

/**
 * Same-origin requests in dev are proxied to the API (see webpack.config.js).
 */
export async function apiRequest<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  const { jsonBody, skipAuth, headers: initHeaders, ...rest } = options;
  const headers = new Headers(initHeaders);
  const token = skipAuth ? null : getStoredAccessToken();
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  if (jsonBody !== undefined && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const response = await fetchApi(path, {
    ...rest,
    headers,
    body: jsonBody !== undefined ? JSON.stringify(jsonBody) : rest.body,
  });
  if (!response.ok) {
    throw new Error(await readErrorMessage(response));
  }
  if (response.status === 204) {
    return undefined as T;
  }
  const ct = response.headers.get("content-type");
  if (ct?.includes("application/json")) {
    return (await response.json()) as T;
  }
  const text = await response.text();
  return text as T;
}